import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Layout from 'components/Form/PVQLocation/Layouts';
import Branch from 'components/Form/Branch';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import PVQName from 'components/Form/PVQName';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import PVQLocation from 'components/Form/PVQLocation';
import { FinancialChildSupportItemModel } from 'forms/sections/financial/childSupport/types';
import NotApplicable from 'components/Form/NotApplicable';

interface Props extends Partial<FinancialChildSupportItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class ChildSupportItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialChildSupportItemModel>) => {
    this.props.onUpdate({
      RecipientName: this.props.RecipientName,
      AmountRequiredToPay: this.props.AmountRequiredToPay,
      AmountPastDue: this.props.AmountPastDue,
      AmountPastDueEstimated: this.props.AmountPastDueEstimated,
      ReasonPastDue: this.props.ReasonPastDue,
      WhenPastDue: this.props.WhenPastDue,
      CurrentlyPastDue: this.props.CurrentlyPastDue,
      WhenBecameCurrent: this.props.WhenBecameCurrent,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenNone: this.props.ActionsTakenNone,
      ActionsTakenReason: this.props.ActionsTakenReason,
      CourtName: this.props.CourtName,
      CourtLocation: this.props.CourtLocation,
      OrderNum: this.props.OrderNum,
      OrderNumIdk: this.props.OrderNumIdk,
      ...queue,
    });
  };

  updateRecipientName = (
    values: FinancialChildSupportItemModel['RecipientName']
  ) => {
    this.update({
      RecipientName: values,
    });
  };

  updateAmountRequiredToPay = (
    values: FinancialChildSupportItemModel['AmountRequiredToPay']
  ) => {
    this.update({
      AmountRequiredToPay: values,
    });
  };

  updateAmountPastDue = (
    values: FinancialChildSupportItemModel['AmountPastDue']
  ) => {
    this.update({
      AmountPastDue: values,
    });
  };

  updateAmountPastDueEstimated = (
    values: FinancialChildSupportItemModel['AmountPastDueEstimated']
  ) => {
    this.update({
      AmountPastDueEstimated: values,
    });
  };

  updateReasonPastDue = (
    values: FinancialChildSupportItemModel['ReasonPastDue']
  ) => {
    this.update({
      ReasonPastDue: values,
    });
  };

  updateWhenPastDue = (
    values: FinancialChildSupportItemModel['WhenPastDue']
  ) => {
    this.update({
      WhenPastDue: values,
    });
  };

  updateCurrentlyPastDue = (
    values: FinancialChildSupportItemModel['CurrentlyPastDue']
  ) => {
    this.update({
      CurrentlyPastDue: values,
    });
  };

  updateWhenBecameCurrent = (
    values: FinancialChildSupportItemModel['WhenBecameCurrent']
  ) => {
    this.update({
      WhenBecameCurrent: values,
    });
  };

  updateActionsTaken = (values: FinancialChildSupportItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: values,
    });
  };

  updateActionsTakenReason = (values: FinancialChildSupportItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  updateActionsTakenNone = (values: FinancialChildSupportItemModel['ActionsTakenNone']) => {
    this.update({
      ActionsTakenNone: values,
      ActionsTakenReason: values?.applicable ? {} as FinancialChildSupportItemModel['ActionsTakenReason'] : this.props?.ActionsTakenReason,
    });
  };

  updateCourtName = (
    values: FinancialChildSupportItemModel['CourtName']
  ) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtLocation = (
    values: FinancialChildSupportItemModel['CourtLocation']
  ) => {
    this.update({
      CourtLocation: values,
    });
  };

  updateOrderNum = (
    values: FinancialChildSupportItemModel['OrderNum']
  ) => {
    this.update({
      OrderNum: values,
    });
  };

  updateOrderNumIdk = (
    values: FinancialChildSupportItemModel['OrderNumIdk']
  ) => {
    this.update({
      OrderNumIdk: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div className="childsupport-item">
        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.recipientName')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RecipientName`}
        >
          <PVQName
            {...this.props.RecipientName}
            attribute={`${attribute}.RecipientName`}
            exactAttribute
            aria-label="Provide full name"
            name="name"
            onUpdate={this.updateRecipientName}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showLastFirstMiddleNameOrder
          />
        </Field>
        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.amountRequiredToPayCurrency')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountRequiredToPay`}
        >
          <Currency
            {...this.props.AmountRequiredToPay}
            maxLength="10"
            attribute={`${attribute}.AmountRequiredToPay`}
            name="amountrequired"
            onUpdate={this.updateAmountRequiredToPay}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>
        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.amountPastDueCurrency')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountPastDue`}
        >
          <Currency
            maxLength="10"
            {...this.props.AmountPastDue}
            attribute={`${attribute}.AmountPastDue`}
            aria-label="Provide amount past due"
            name="amountpastdue"
            onUpdate={this.updateAmountPastDue}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
          <Checkbox
            name="amountpastdueestimated"
            label={i18n.t('financial.pvq.shared.estimated')}
            {...this.props.AmountPastDueEstimated}
            onUpdate={this.updateAmountPastDueEstimated}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.AmountPastDueEstimated`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.whyTextArea')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ReasonPastDue`}
        >
          <Textarea
            {...this.props.ReasonPastDue}
            attribute={`${attribute}.ReasonPastDue`}
            name="whypastdue"
            onUpdate={this.updateReasonPastDue}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.whenDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.WhenPastDue`}
        >
          <DateControl
            {...this.props.WhenPastDue}
            attribute={`${attribute}.WhenPastDue`}
            name="whenpastdue"
            onUpdate={this.updateWhenPastDue}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            scrollIntoView={this.props.scrollIntoView}
            showEstimated={true}
          />
        </Field>

        <Branch
          {...this.props.CurrentlyPastDue}
          name="currently_past_due"
          label={i18n.t('financial.pvq.childSupport.branch.ifYes.branch.title')}
          labelSize="h4"
          className="childsupport-current-branch"
          onUpdate={this.updateCurrentlyPastDue}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.CurrentlyPastDue`}
        />

        {this.props.CurrentlyPastDue?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.childSupport.branch.ifYes.branch.ifNo.whenDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenBecameCurrent`}
          >
            <DateControl
              {...this.props.WhenBecameCurrent}
              attribute={`${attribute}.WhenBecameCurrent`}
              name="whenbecamecurrent"
              onUpdate={this.updateWhenBecameCurrent}
              onError={this.props.onError}
              hideDay
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated={true}
            />
          </Field>
        )}

        {this.props.CurrentlyPastDue?.value === 'Yes' && (

          <Field
            title={i18n.t('financial.pvq.childSupport.branch.ifYes.branch.ifYes.combobox.actionsTaken')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <NotApplicable
              {...this.props.ActionsTakenNone}
              label={i18n.t('financial.pvq.shared.none')}
              attribute={`${attribute}.ActionsTakenNone`}
              onUpdate={this.updateActionsTakenNone}
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
            >
              <Textarea
                {...this.props.ActionsTaken}
                attribute={`${attribute}.ActionsTaken`}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                onUpdate={this.updateActionsTaken}
                required={this.props.required}
              />
            </NotApplicable>
          </Field>
        )}

        {this.props.ActionsTakenNone?.applicable === false && this.props.CurrentlyPastDue?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.childSupport.branch.ifYes.branch.ifYes.combobox.ifNoneTextArea')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTakenReason`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTakenReason`}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateActionsTakenReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.courtNameText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <Text
            {...this.props.CourtName}
            attribute={`${attribute}.CourtName`}
            name="courtname"
            onUpdate={this.updateCourtName}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.courtLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtLocation`}
        >
          <PVQLocation
            {...this.props.CourtLocation}
            attribute={`${attribute}.CourtLocation`}
            name="courtlocation"
            layout={Layout.US_STREET_CITY_STATE_ZIP}
            onUpdate={this.updateCourtLocation}
            onError={this.props.onError}
            required={this.props.required}
            inUSA
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.childSupport.branch.ifYes.orderNumTextIdk')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderNum`}
        >
          <NotApplicable
            {...this.props.OrderNumIdk}
            name="ordernumidk"
            label={i18n.t('financial.pvq.shared.idk')}
            onUpdate={this.updateOrderNumIdk}
            onError={this.props.onError}
            attribute={`${attribute}.OrderNumIdk`}
          >
            <Text
              {...this.props.OrderNum}
              attribute={`${attribute}.OrderNum`}
              name="ordernum"
              onUpdate={this.updateOrderNum}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>
      </div>
    );
  }
}
ChildSupportItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  required: false,
  RecipientName: {} as PVQNameModel,
  AmountRequiredToPay: {} as FieldModel,
  AmountPastDue: {} as FieldModel,
  AmountPastDueEstimated: {} as CheckboxModel,
  ReasonPastDue: {} as FieldModel,
  WhenPastDue: {} as DateModel,
  CurrentlyPastDue: {} as YesOrNoModel,
  WhenBecameCurrent: {} as DateModel,
  ActionsTaken: {} as FieldModel,
  ActionsTakenNone: {} as ApplicableModel,
  ActionsTakenReason: {} as FieldModel,
  CourtName: {} as FieldModel,
  CourtLocation: {} as LocationModel,
  OrderNum: {} as FieldModel,
  OrderNumIdk: {} as ApplicableModel
};
